import { useMemo } from 'react';
import { useTable } from 'spacetimedb/react';
import { tables as minetwinTables } from '@sdk/minetwin';
import type { AppRole } from '@/generated/minetwin/types';

/**
 * Workforce roster from the MineTwin module.
 *
 * Phase 3 of the Workforce Alignment Plan collapsed `employee` + `user` into
 * a single `user` table; this hook (renamed from `useEmployees`) reads the
 * unified workforce. It unions:
 *
 *   - `visible_users`: people who have signed in at least once. The User
 *     row carries `app_role`, `occupation`, `email`, `userId`, plus the
 *     bind-side STDB identity.
 *   - `visible_pending_users`: people a coordinator added by email who
 *     have not signed in yet. Each pending row carries email + project_id +
 *     occupation; on first connect `client_connected` drains them into
 *     real User + UserSite rows.
 *
 * Both views are scoped server-side via `admin_project_ids` — OrgAdmin /
 * OrgViewer see every project; Admin sees their attached sites; Member /
 * Contractor see nothing roster-wise (their own data is in `my_*`).
 *
 * `visible_user_sites` is the join lookup so each row carries the set of
 * `projectIds` it belongs to. Pending users carry a single project_id
 * (their staging row) and are surfaced with `bound = false`.
 */
export type UserRow = {
  /** Stable identity. For pending users this is `pending-<email>` — the
   *  real `user_id` arrives on first connect. */
  userId: string | null;
  email: string;
  displayName: string;
  /** Permission tier from the JWT, written every connect. `null` for
   *  pending users (no JWT yet). */
  appRole: AppRole | null;
  /** Mine job — drives orientation assignment. Empty string until set. */
  occupation: string;
  /** Project IDs the user belongs to (active site memberships). Pending
   *  users carry exactly one (their staging project_id). */
  projectIds: string[];
  /** True once the user has signed in (has a real User row). False for
   *  rows surfaced from `visible_pending_users`. */
  bound: boolean;
  /** ms since epoch — sorts the roster. For active users this is `addedAt`;
   *  for pending rows it's `pendingCreatedAt`. */
  addedAt: number;
};

export function useUsers(): { users: UserRow[]; isReady: boolean } {
  const [activeRows, activeReady] = useTable(
    'minetwin',
    minetwinTables.visible_users,
  );
  const [pendingRows, pendingReady] = useTable(
    'minetwin',
    minetwinTables.visible_pending_users,
  );
  const [siteRows, sitesReady] = useTable(
    'minetwin',
    minetwinTables.visible_user_sites,
  );

  const projectIdsByUser = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const row of siteRows) {
      if (!row.active) continue;
      const list = m.get(row.userId) ?? [];
      list.push(row.projectId);
      m.set(row.userId, list);
    }
    return m;
  }, [siteRows]);

  const users = useMemo<UserRow[]>(() => {
    // Dedupe active rows by `userId`. Two compounding causes both observed
    // by Cloud Lead 2026-06-24:
    //
    //   (a) "same user appears twice when navigating" — `visible_users` is a
    //       per-site-membership view server-side; a user on N sites returns
    //       N copies.
    //   (b) "Set occupation doesn't update the row at runtime" — the view has
    //       no primary key (`generated/minetwin/index.ts: visible_users` has
    //       `indexes: [], constraints: []`), so the SDK's `table.onUpdate?`
    //       is a no-op (optional-chain skips it for keyless tables). Server
    //       row updates arrive as fresh INSERTs; the old row sticks in the
    //       cache. Without this dedupe the table either shows two rows for
    //       the same user, or — if a downstream filter happens to drop one —
    //       picks the stale one.
    //
    // Resolution: keep the row with the LATEST `updatedAt`. That column is
    // written on every server-side mutation (see
    // `modules/minetwin/src/reducers/workforce.rs: set_user_occupation`
    // bumps `user.updated_at = ctx.timestamp`), so most-recent wins
    // correctly reflects the post-update state.
    const activeByUserId = new Map<string, (typeof activeRows)[number]>();
    for (const r of activeRows) {
      const existing = activeByUserId.get(r.userId);
      if (!existing || toMillis(r.updatedAt) > toMillis(existing.updatedAt)) {
        activeByUserId.set(r.userId, r);
      }
    }
    const out: UserRow[] = Array.from(activeByUserId.values()).map((r) => ({
      userId: r.userId,
      email: r.email,
      displayName: r.displayName,
      appRole: r.appRole,
      occupation: r.occupation,
      projectIds: projectIdsByUser.get(r.userId) ?? [],
      bound: true,
      addedAt: toMillis(r.addedAt),
    }));
    const activeEmails = new Set(out.map((u) => u.email.toLowerCase()));

    // Append pending rows. Multiple pending rows for the same email on
    // different sites collapse into a single UserRow carrying both
    // project_ids (matches the active-user representation).
    const pendingByEmail = new Map<
      string,
      {
        email: string;
        occupation: string;
        projectIds: string[];
        addedAt: number;
      }
    >();
    for (const p of pendingRows) {
      const existing = pendingByEmail.get(p.email);
      if (existing) {
        existing.projectIds.push(p.projectId);
        existing.addedAt = Math.min(existing.addedAt, toMillis(p.createdAt));
        if (!existing.occupation && p.occupation) {
          existing.occupation = p.occupation;
        }
      } else {
        pendingByEmail.set(p.email, {
          email: p.email,
          occupation: p.occupation,
          projectIds: [p.projectId],
          addedAt: toMillis(p.createdAt),
        });
      }
    }
    for (const p of pendingByEmail.values()) {
      // Drop pending entries whose email already appears in the active set
      // — server doesn't always drain `pending_user` on first connect, and
      // the unmerged row otherwise duplicates the now-signed-in user.
      if (activeEmails.has(p.email.toLowerCase())) continue;
      out.push({
        userId: null,
        email: p.email,
        displayName: p.email,
        appRole: null,
        occupation: p.occupation,
        projectIds: p.projectIds,
        bound: false,
        addedAt: p.addedAt,
      });
    }

    return out.sort((a, b) => a.email.localeCompare(b.email));
  }, [activeRows, pendingRows, projectIdsByUser]);

  return {
    users,
    isReady: activeReady && pendingReady && sitesReady,
  };
}

/**
 * SpacetimeDB timestamps can arrive as a `Date`, a raw number, or a
 * `{ micros: bigint }` depending on SDK serialization. Coerce defensively.
 */
function toMillis(t: unknown): number {
  if (typeof t === 'number') return t;
  if (t instanceof Date) return t.getTime();
  if (t && typeof t === 'object') {
    const anyT = t as { micros?: bigint; getTime?: () => number };
    if (typeof anyT.getTime === 'function') return anyT.getTime();
    if (typeof anyT.micros === 'bigint') return Number(anyT.micros / 1000n);
  }
  return 0;
}
